/**
 * Synchronous name/code lookup for the languages the translate API accepts.
 *
 * The list is fetched at runtime, but attribute validation is synchronous and
 * sits on the variant grid's hot path — importing the store there would drag
 * `baseService` -> `appStore` into a module that deliberately has no store
 * imports. So the store pushes its result in here instead.
 *
 * Until that fetch lands the registry is empty and every value validates: a
 * column conversion must never block on a network call the user did not ask for.
 */
import type { Language } from '@/types/shared/language';

/**
 * What an empty Language cell means. Deliberately separate from the AI bar's
 * `DEFAULT_TARGET_LANGUAGE` — that one seeds a dropdown, this one defines the
 * value written into unfilled feed cells.
 */
export const DEFAULT_LANGUAGE_NAME = 'English';

let entries = new Map<string, Language>();
const listeners = new Set<() => void>();

/** Fires after every write, so callers can drop anything they memoised. */
export function onLanguageRegistryChange(listener: () => void): void {
  listeners.add(listener);
}

export function setLanguageRegistry(languages: readonly Language[]): void {
  const next = new Map<string, Language>();
  for (const language of languages) {
    const name = normalizeLanguageValue(language.name ?? '');
    const code = normalizeLanguageValue(language.code ?? '');
    if (name) next.set(name, language);
    if (code) next.set(code, language);
  }
  entries = next;
  for (const listener of listeners) listener();
}

export function isLanguageRegistryEmpty(): boolean {
  return entries.size === 0;
}

export function findLanguage(value: string): Language | undefined {
  return entries.get(normalizeLanguageValue(value));
}

/**
 * Identity of a language inside a dimension key — the code, never the display
 * name, because `buildDimensionKey` requires facet values free of `|` and `:`.
 */
export function languageCodeFor(value: string): string | undefined {
  return findLanguage(value)?.code;
}

/** Canonical display name — what the translate API expects as a target. */
export function languageNameFor(value: string): string | undefined {
  return findLanguage(value)?.name;
}

function normalizeLanguageValue(value: string): string {
  return value.trim().toLowerCase();
}
